import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import { format, subDays, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import { toInclusiveDayRange } from "../utils/dateUtils";

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  compareValue?: DateRange;
  onCompareChange?: (range: DateRange | null) => void;
  showCompare?: boolean;
}

type PresetKey = "allTime" | "7d" | "14d" | "30d" | "thisMonth" | "lastMonth" | "custom";

const DateRangePicker = ({
  value,
  onChange,
  compareValue,
  onCompareChange,
  showCompare = false,
}: DateRangePickerProps) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [activePreset, setActivePreset] = useState<PresetKey>("allTime");
  const [isCompareEnabled, setIsCompareEnabled] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const locale = i18n.language === "fr" ? fr : enUS;

  const presets: { key: PresetKey; label: string; getRange: () => DateRange }[] = [
    {
      key: "allTime",
      label: t("statsNew.dateRange.allTime"),
      getRange: () => ({
        startDate: null,
        endDate: null,
      }),
    },
    {
      key: "7d",
      label: t("statsNew.dateRange.last7Days"),
      getRange: () => toInclusiveDayRange(subDays(new Date(), 6), new Date()),
    },
    {
      key: "14d",
      label: t("statsNew.dateRange.last14Days"),
      getRange: () => toInclusiveDayRange(subDays(new Date(), 13), new Date()),
    },
    {
      key: "30d",
      label: t("statsNew.dateRange.last30Days"),
      getRange: () => toInclusiveDayRange(subDays(new Date(), 29), new Date()),
    },
    {
      key: "thisMonth",
      label: t("statsNew.dateRange.thisMonth"),
      getRange: () => toInclusiveDayRange(startOfMonth(new Date()), new Date()),
    },
    {
      key: "lastMonth",
      label: t("statsNew.dateRange.lastMonth"),
      getRange: () =>
        toInclusiveDayRange(
          startOfMonth(subMonths(new Date(), 1)),
          endOfMonth(subMonths(new Date(), 1)),
        ),
    },
    {
      key: "custom",
      label: t("statsNew.dateRange.custom"),
      getRange: () => value,
    },
  ];

  // Detect active preset from value
  useEffect(() => {
    if (!value.startDate && !value.endDate) {
      setActivePreset("allTime");
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePresetClick = (preset: typeof presets[0]) => {
    setActivePreset(preset.key);
    if (preset.key !== "custom") {
      onChange(preset.getRange());
    }
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    onChange(toInclusiveDayRange(start, end));
    setActivePreset("custom");
  };

  const handleCompareToggle = () => {
    const newEnabled = !isCompareEnabled;
    setIsCompareEnabled(newEnabled);
    if (!newEnabled && onCompareChange) {
      onCompareChange(null);
    }
  };

  const formatDateRange = (range: DateRange): string => {
    // All time case
    if (!range.startDate && !range.endDate) {
      return t("statsNew.dateRange.allTime");
    }
    if (!range.startDate || !range.endDate) {
      return t("statsNew.dateRange.selectDates");
    }
    return `${format(range.startDate, "dd MMM yyyy", { locale })} - ${format(
      range.endDate,
      "dd MMM yyyy",
      { locale },
    )}`;
  };

  const isAllTime = activePreset === "allTime";

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="text-sm font-medium text-gray-700">{formatDateRange(value)}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-4 min-w-[600px]">
          <div className="flex gap-4">
            {/* Presets sidebar */}
            <div className="flex flex-col gap-1 border-r border-gray-200 pr-4 min-w-[160px]">
              {presets.map((preset) => (
                <button
                  key={preset.key}
                  onClick={() => handlePresetClick(preset)}
                  className={`px-3 py-2 text-sm text-left rounded-lg transition-colors ${
                    activePreset === preset.key
                      ? "bg-green-100 text-green-700 font-medium"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {preset.label}
                </button>
              ))}

              {showCompare && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isCompareEnabled}
                      onChange={handleCompareToggle}
                      className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-600">
                      {t("statsNew.dateRange.comparePeriod")}
                    </span>
                  </label>
                </div>
              )}
            </div>

            {/* Calendar - hidden when "All time" is selected */}
            <div className="flex-1">
              {isAllTime ? (
                <div className="flex items-center justify-center h-full min-h-[280px] text-gray-500">
                  <div className="text-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-12 w-12 mx-auto mb-3 text-gray-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-sm">{t("statsNew.dateRange.allTimeDescription")}</p>
                  </div>
                </div>
              ) : (
                <>
                  <DatePicker
                    selected={value.startDate}
                    onChange={handleDateChange}
                    startDate={value.startDate}
                    endDate={value.endDate}
                    selectsRange
                    inline
                    monthsShown={2}
                    locale={locale}
                    maxDate={new Date()}
                    calendarClassName="stats-datepicker"
                  />

                  {isCompareEnabled && compareValue && onCompareChange && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        {t("statsNew.dateRange.compareTo")}
                      </p>
                      <DatePicker
                        selected={compareValue.startDate}
                        onChange={(dates: [Date | null, Date | null]) => {
                          onCompareChange(toInclusiveDayRange(dates[0], dates[1]));
                        }}
                        startDate={compareValue.startDate}
                        endDate={compareValue.endDate}
                        selectsRange
                        inline
                        monthsShown={2}
                        locale={locale}
                        maxDate={value.startDate || new Date()}
                        calendarClassName="stats-datepicker"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
